import React, { useState, useEffect } from 'react';
import ProductForm from './ProductForm';
import { toast } from 'react-toastify';
import { PencilSquareIcon, TrashIcon, PlusCircleIcon, ExclamationTriangleIcon, ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api'; // 1. YENİ: Merkezi api yardımcısını import ettik.

function ProductManagementPanel({ openModal, closeModal }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  const isPriceOld = (updateDate, warningDays = 30) => {
    if (!updateDate) return false;
    const warningPeriodInMs = (warningDays || 30) * 24 * 60 * 60 * 1000;
    const differenceInMs = new Date() - new Date(updateDate);
    return differenceInMs > warningPeriodInMs;
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // 2. DEĞİŞİKLİK: fetch, response.ok ve response.json() satırları yerine tek satırda api() kullandık.
      const data = await api(`/api/products`);
      const productList = data?.products || data || [];
      setProducts(productList);
    } catch (err) {
      setError(err.message);
      toast.error(`Ürünleri yüklerken hata oluştu: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const handleDeleteProduct = (productId) => {
    openModal(
      <div className="p-4 text-center">
        <ExclamationTriangleIcon className="mx-auto h-16 w-16 text-red-600" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">Emin Misiniz?</h3>
        <p className="mt-2 text-sm text-gray-500">Bu ürünü silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</p>
        <div className="mt-6 flex justify-center space-x-4">
          <button type="button" className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700"
            onClick={async () => {
              if (!token) { toast.error("Yetki bulunamadı."); return; }
              try {
                // 3. DEĞİŞİKLİK: Ürün silme isteğini de merkezi api() fonksiyonu ile yapıyoruz.
                await api.delete(`/api/products/${productId}`);

                toast.success('Ürün başarıyla silindi!');
                fetchProducts();
                closeModal();
              } catch (err) { 
                toast.error(`Ürünü silerken hata oluştu: ${err.message}`); 
                closeModal(); 
              }
            }}
          >Sil</button>
          <button type="button" className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50" onClick={closeModal}>İptal</button>
        </div>
      </div>
    );
  };
  
  if (loading) return <div className="text-center p-10">Ürünler yükleniyor...</div>;
  if (error) return <div className="text-center p-10 text-red-600">Hata: {error}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Modern Header */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-text-dark mb-3 tracking-tight">Ürün Yönetimi</h1>
          <p className="text-text-light text-lg max-w-md mx-auto">Ürünlerinizi ekleyin, düzenleyin ve stok durumlarını takip edin</p>
        </div>

        <div className="flex justify-end mb-8">
          <button
            onClick={() => openModal(<ProductForm closeModal={closeModal} fetchProducts={fetchProducts} />)}
            className="bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center space-x-3"
          >
            <PlusCircleIcon className="h-6 w-6" />
            <span className="text-lg">Yeni Ürün Ekle</span>
          </button>
        </div>
        {/* MOBİL UYUMLU LİSTELEME */}
        <div className="space-y-6 md:hidden">
            {products.map((product, index) => (
              <div 
                key={product.id} 
                className="group bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.01]"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="relative">
                      <img 
                        className="h-24 w-24 rounded-2xl object-cover shadow-lg transition-transform duration-300 group-hover:scale-105" 
                        src={product.main_image_url || 'https://placehold.co/96x96/e0e0e0/ffffff?text=Yok'} 
                        alt={product.name}
                      />
                      {isPriceOld(product.price_last_updated, product.price_warning_days) && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                          <ClockIcon className="h-3 w-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="text-xl font-bold text-gray-900 group-hover:text-brand-green transition-colors">{product.name}</h3>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <button 
                            onClick={() => openModal(<ProductForm closeModal={closeModal} fetchProducts={fetchProducts} product={product} />)} 
                            className="p-3 rounded-2xl bg-brand-green text-white hover:bg-brand-green-light transition-all duration-200 transform hover:scale-105 shadow-lg"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product.id)} 
                            className="p-3 rounded-2xl bg-red-500 text-white hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mb-4">
                        <p className="text-2xl font-bold text-brand-green">₺{parseFloat(product.price).toFixed(2)}</p>
                        {isPriceOld(product.price_last_updated, product.price_warning_days) && (
                          <div className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full" title={`Fiyat ${product.price_warning_days} günden eskidir!`}>
                            <ClockIcon className="h-4 w-4 mr-1" /> 
                            Fiyatı Güncelle
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Stok Durumu:
                    </h4>
                    {product.variants && product.variants.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {product.variants.map((variant, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                            <span className="text-sm font-medium text-gray-700">{variant.color} - {variant.size}</span>
                            <span className={`font-bold px-3 py-1 rounded-full text-sm ${
                              variant.stock <= product.min_stock 
                                ? 'bg-red-100 text-red-800 border border-red-200' 
                                : 'bg-green-100 text-green-800 border border-green-200'
                            }`}>
                              {variant.stock} adet
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className='text-sm text-gray-400 italic'>Varyant bulunamadı</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
        </div>
        {/* GENİŞ EKRAN TABLO GÖRÜNÜMÜ */}
        <div className="hidden md:block bg-white rounded-3xl shadow-2xl border border-gray-100 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Ürün Bilgisi</th>
                    <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Fiyat</th>
                    <th className="px-8 py-6 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">Stok Durumu</th>
                    <th className="px-8 py-6 text-right text-sm font-bold text-gray-700 uppercase tracking-wider">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product, index) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition-colors duration-200" style={{ animationDelay: `${index * 0.05}s` }}>
                      <td className="px-8 py-6 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-20 w-20">
                            <img className="h-20 w-20 rounded-2xl object-cover shadow-lg" src={product.main_image_url || `https://placehold.co/80x80/e0e0e0/ffffff?text=Yok`} alt={product.name}/>
                          </div>
                          <div className="ml-6">
                            <div className="flex items-center gap-3">
                              <div className="text-lg font-bold text-gray-900">{product.name}</div>
                              {isPriceOld(product.price_last_updated, product.price_warning_days) && (
                                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center" title={`Bu ürünün fiyatı ${product.price_warning_days} günden uzun süredir güncellenmedi!`}>
                                  <ClockIcon className="h-4 w-4 text-white" />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap">
                        <span className="text-2xl font-bold text-brand-green">₺{parseFloat(product.price).toFixed(2)}</span>
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap">
                        {product.variants && product.variants.length > 0 ? (
                          <div className="space-y-2">
                            {product.variants.map((variant, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <span className="text-sm font-medium">{variant.color} - {variant.size}</span>
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold px-2 py-1 rounded-full text-xs ${
                                    variant.stock <= product.min_stock ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {variant.stock} adet
                                  </span>
                                  {variant.stock <= product.min_stock && (
                                    <ExclamationCircleIcon className="h-4 w-4 text-red-600" title={`Kritik stok seviyesi! Minimum: ${product.min_stock} adet olmalı.`} />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className='text-sm text-gray-400 italic'>Varyant yok</span>
                        )}
                      </td>
                      <td className="px-8 py-6 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-3">
                          <button 
                            onClick={() => openModal(<ProductForm closeModal={closeModal} fetchProducts={fetchProducts} product={product} />)} 
                            className="p-3 rounded-2xl bg-brand-green text-white hover:bg-brand-green-light transition-all duration-200 transform hover:scale-105 shadow-lg" 
                            title="Düzenle"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                          <button 
                            onClick={() => handleDeleteProduct(product.id)} 
                            className="p-3 rounded-2xl bg-red-500 text-white hover:bg-red-600 transition-all duration-200 transform hover:scale-105 shadow-lg" 
                            title="Sil"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        </div>
      </div>
    </div>
  );
}

export default ProductManagementPanel;